import { inject } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ErrorBoundary } from 'bernie-view';

import { ActivityRecommendations } from '@shared-ui/local-expert-activities';
import { ShortlistToggleProvider } from '@shared-ui/trip-planning-travelpulse';
import { useBexApi } from '@shared-ui/bex-api-context';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { UIPageStore } from 'stores/ui-page-store';

import { Level, liveAnnounce } from 'uitk-react-utils';

import { DateUtils } from 'utils/date-utils';

import { Lazy } from 'components/common/lazy/lazy';

interface ActivityRecommendationsWrapperProps {
  currentHotel?: CurrentHotelStore;
  uiPage?: UIPageStore;
}

export function onToggleClick(uiPage) {
  const toggleDispatch = ({ message }) => {
    if (typeof uiPage?.showNotification === 'function') {
      uiPage.showNotification(message);
      liveAnnounce(message, Level.POLITE);
    }
  };

  return toggleDispatch;
}

export const ActivityRecommendationsWrapper: React.FunctionComponent<ActivityRecommendationsWrapperProps> = inject(
  'currentHotel',
  'uiPage'
)(({ currentHotel, uiPage }) => {
  const { context } = useBexApi();

  const { offerSearchData, hotel } = currentHotel || {};
  const { overview } = hotel || {};

  if (!hotel || !overview) {
    return null;
  }

  const latitude = parseFloat(overview?.latitude);
  const longitude = parseFloat(overview?.longitude);
  const toggleDispatch = onToggleClick(uiPage);

  const dateRanges =
    offerSearchData?.startDate && offerSearchData?.endDate
      ? {
          startDate: DateUtils.parseDateString(offerSearchData.startDate),
          endDate: DateUtils.parseDateString(offerSearchData.endDate),
        }
      : null;

  return (
    <ErrorBoundary label="hotel.information.activities">
      <>
        {context && currentHotel?.hotelId && (
          <Lazy>
            <Lazy.Placeholder>
              <div data-stid="recommended-activities__placeholder" />
            </Lazy.Placeholder>
            <Lazy.Loaded>
              <AbacusExperiment name="LX_ACTIVITIES_NEARBY_HOTEL">
                <AbacusControl />
                <AbacusVariant bucket={1}>
                  <ShortlistToggleProvider dispatchHandler={toggleDispatch}>
                    <ActivityRecommendations
                      inputs={{
                        destination: {
                          regionId: String(currentHotel.hotelId),
                          regionType: 'hotel',
                          coordinates: {
                            latitude,
                            longitude,
                          },
                        },
                        dateRange: dateRanges,
                        searchViewOptions: ['TRAVEL_DISTANCE_FROM_HOTEL' as any],
                      }}
                      skipSsr={true}
                    />
                  </ShortlistToggleProvider>
                </AbacusVariant>
              </AbacusExperiment>
            </Lazy.Loaded>
          </Lazy>
        )}
      </>
    </ErrorBoundary>
  );
});
